import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpen,
  LifeBuoy,
  MessageSquarePlus,
} from "lucide-react";
import { Modal } from "./Modal";
import { useProjects } from "../data/ProjectsContext";
import { useToast } from "../toast/ToastContext";
import {
  apiErrorMessage,
  cloudCreateSupportRequest,
  cloudListSupportRequests,
  type SupportRequestDto,
} from "../api/client";

const CATEGORIES: { id: string; label: string; hint: string }[] = [
  { id: "account", label: "账号权限", hint: "登录、成员、角色" },
  { id: "billing", label: "套餐计费", hint: "配额、升级、账单" },
  { id: "capability", label: "能力开通", hint: "API、AI、增值" },
  { id: "debug_assist", label: "代调试协作", hint: "远程协助、授权" },
  { id: "other", label: "其他", hint: "其余问题" },
];

const STATUS_LABEL: Record<string, string> = {
  open: "待处理",
  in_progress: "处理中",
  waiting_integrator: "待回复",
  resolved: "已解决",
  cancelled: "已取消",
};

const PATH_TITLES: Record<string, string> = {
  "/dashboard": "工作台",
  "/projects": "项目列表",
  "/tickets": "运维工单",
  "/resources": "安装指引",
  "/devices": "硬件设备",
  "/users": "人员管理",
  "/roles": "角色管理",
  "/audit": "操作日志",
  "/certification": "企业认证",
  "/settings": "个人中心",
  "/billing": "套餐",
  "/support-requests": "平台求助",
  "/notifications": "通知中心",
};

/** Infer page title from path for help context (FAB + future KB). */
export function supportPageTitleFromPath(pathname: string): string {
  if (pathname.startsWith("/projects/") && pathname.includes("/debug")) {
    return "项目调试";
  }
  if (/^\/projects\/[^/]+/.test(pathname)) return "项目详情";
  if (PATH_TITLES[pathname]) return PATH_TITLES[pathname];
  for (const [prefix, title] of Object.entries(PATH_TITLES)) {
    if (pathname.startsWith(`${prefix}/`)) return title;
  }
  return "控制台";
}

type PanelTab = "ask" | "mine" | "kb";

/**
 * Integrator-only floating help entry. Captures current route as context
 * for ops + future knowledge-base answers.
 */
export function SupportHelpFab() {
  const location = useLocation();
  const { list: projects } = useProjects();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<PanelTab>("ask");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState<SupportRequestDto[]>([]);
  const [form, setForm] = useState({
    title: "",
    category: "other",
    description: "",
    projectId: "",
  });

  const pagePath = `${location.pathname}${location.search || ""}`;
  const pageTitle = useMemo(
    () => supportPageTitleFromPath(location.pathname),
    [location.pathname],
  );

  const projectFromRoute = useMemo(() => {
    const m = location.pathname.match(/^\/projects\/([^/]+)/);
    return m?.[1] || "";
  }, [location.pathname]);

  const loadRecent = useCallback(async () => {
    try {
      const res = await cloudListSupportRequests("openish");
      setRecent((res.items || []).slice(0, 8));
    } catch {
      setRecent([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadRecent();
    setForm((f) => ({
      ...f,
      projectId: f.projectId || projectFromRoute || "",
    }));
  }, [open, loadRecent, projectFromRoute]);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast("请填写标题与描述");
      return;
    }
    setBusy(true);
    try {
      await cloudCreateSupportRequest({
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim(),
        projectId: form.projectId || null,
        pagePath,
        pageTitle,
      });
      toast("求助已提交，平台运营会尽快处理");
      setForm({
        title: "",
        category: "other",
        description: "",
        projectId: projectFromRoute || "",
      });
      setTab("mine");
      await loadRecent();
    } catch (err) {
      toast(apiErrorMessage(err, "提交失败"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="support-fab"
        aria-label="平台求助"
        title="平台求助"
        onClick={() => {
          setTab("ask");
          setOpen(true);
        }}
      >
        <LifeBuoy size={22} strokeWidth={1.85} aria-hidden />
      </button>

      <Modal
        open={open}
        title="平台求助"
        eyebrow="随时提问 · 带上当前页面上下文"
        drawer
        onClose={() => setOpen(false)}
        className="support-fab-drawer"
      >
        <div className="support-fab-context" aria-live="polite">
          <span className="support-fab-context__lbl">当前页面</span>
          <strong>{pageTitle}</strong>
          <code className="support-fab-context__path">{pagePath}</code>
        </div>

        <div className="support-fab-tabs" role="tablist">
          {(
            [
              ["ask", "发起求助", MessageSquarePlus],
              ["mine", "我的工单", LifeBuoy],
              ["kb", "知识库", BookOpen],
            ] as const
          ).map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`support-fab-tab${tab === id ? " active" : ""}`}
              onClick={() => setTab(id)}
            >
              <Icon size={14} aria-hidden />
              {label}
            </button>
          ))}
        </div>

        {tab === "ask" ? (
          <div className="support-fab-ask">
            <div className="field">
              <label>分类</label>
              <div className="support-fab-cats">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`support-fab-cat${form.category === c.id ? " active" : ""}`}
                    onClick={() => setForm({ ...form, category: c.id })}
                  >
                    <strong>{c.label}</strong>
                    <span>{c.hint}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label>标题 *</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={`例如：${pageTitle} 相关问题`}
              />
            </div>
            <div className="field">
              <label>关联项目（可选）</label>
              <select
                value={form.projectId}
                onChange={(e) =>
                  setForm({ ...form, projectId: e.target.value })
                }
              >
                <option value="">不关联</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>描述 *</label>
              <textarea
                rows={5}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="说明现象、期望与已尝试步骤。页面上下文会自动附带。"
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setOpen(false)}
              >
                取消
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void submit()}
              >
                提交求助
              </button>
            </div>
          </div>
        ) : null}

        {tab === "mine" ? (
          <div className="support-fab-mine">
            {recent.length === 0 ? (
              <p className="muted empty-state" style={{ padding: "24px 0" }}>
                暂无未关闭求助
              </p>
            ) : (
              <ul className="support-fab-list">
                {recent.map((r) => (
                  <li key={r.id}>
                    <strong>{r.title}</strong>
                    <span className="muted">
                      {STATUS_LABEL[r.status] || r.status}
                      {r.pageTitle ? ` · ${r.pageTitle}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link
              className="btn btn-ghost"
              to="/support-requests"
              onClick={() => setOpen(false)}
            >
              查看全部求助
            </Link>
          </div>
        ) : null}

        {tab === "kb" ? (
          <div className="support-fab-kb">
            <div className="support-fab-kb__card">
              <BookOpen size={20} aria-hidden />
              <div>
                <strong>知识库即将接入</strong>
                <p className="muted">
                  后续将按「当前页面 + 分类」推荐文章与标准答疑；现在可直接发起求助，运营侧会看到你所在的功能页面。
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setTab("ask")}
            >
              去发起求助
            </button>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
